/**
 * PublishArchiveModal — admin editor for the post-class archive.
 *
 * Fields:
 *   - Topics summary (plain text, 2-3 lines suggested)
 *   - Recording embed HTML (paste from Dadan/YouTube/etc., server-side sanitized)
 *   - Publish / Unpublish action
 *
 * At least ONE of (summary, recording) must be present to publish.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Send, Eye, EyeOff, Info, AlertTriangle, MessagesSquare, ExternalLink, ThumbsUp, CheckCircle2, Pin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUMMARY_PLACEHOLDER =
  'Faculty covered: fuel oil system components, common failure modes, and troubleshooting.\n' +
  'Key references: Reed\'s Vol 8 Chapter 4, MEPC.176(58).\n' +
  'Also demoed: emergency shutdown procedure on the simulator.';

const EMBED_PLACEHOLDER =
  '<iframe src="https://player.dadan.io/embed/abc123" width="640" height="360" allowfullscreen></iframe>';

const PublishArchiveModal = ({ open, cls, onClose, onSaved }) => {
  const [summary, setSummary] = useState('');
  const [embed, setEmbed] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [qna, setQna] = useState({ loading: false, total: 0, answered: 0, questions: [], error: '' });

  // Reset drafts when the dialog opens for a new class
  useEffect(() => {
    if (open && cls) {
      setSummary(cls.topics_summary || '');
      setEmbed(cls.recording_embed_html || '');
    }
  }, [open, cls]);

  // Fetch Q&A preview whenever the modal opens for a class
  useEffect(() => {
    if (!open || !cls?.id) return;
    let cancelled = false;
    (async () => {
      try {
        setQna(q => ({ ...q, loading: true, error: '' }));
        const { data } = await axios.get(`${API}/live-classes/${cls.id}/questions`);
        if (cancelled) return;
        setQna({
          loading: false,
          error: '',
          total: data.total || 0,
          answered: data.answered_count || 0,
          questions: (data.questions || []).slice(0, 6),
        });
      } catch (err) {
        if (cancelled) return;
        const d = err.response?.data?.detail;
        setQna({ loading: false, total: 0, answered: 0, questions: [], error: typeof d === 'string' ? d : 'Could not load Q&A' });
      }
    })();
    return () => { cancelled = true; };
  }, [open, cls?.id]);

  if (!cls) return null;

  const isPublished = !!cls.published_at;
  const canPublish = (summary.trim().length > 0) || (embed.trim().length > 0);

  const saveDraft = async () => {
    try {
      setSaving(true);
      const { data } = await axios.put(`${API}/admin/live-classes/${cls.id}`, {
        topics_summary: summary,
        recording_embed_html: embed,
      });
      toast.success('Draft saved');
      onSaved?.(data.class);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    if (!canPublish) {
      toast.error('Add a summary or a recording embed before publishing.');
      return;
    }
    try {
      setPublishing(true);
      // Save current drafts first so publish uses the latest fields.
      await axios.put(`${API}/admin/live-classes/${cls.id}`, {
        topics_summary: summary,
        recording_embed_html: embed,
      });
      const { data } = await axios.post(`${API}/admin/live-classes/${cls.id}/publish`);
      toast.success('Published — students can now see this archive.');
      onSaved?.(data.class);
      onClose?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to publish'));
    } finally {
      setPublishing(false);
    }
  };

  const doUnpublish = async () => {
    if (!window.confirm('Retract this archive from the student-facing list? You can republish anytime.')) return;
    try {
      setPublishing(true);
      const { data } = await axios.post(`${API}/admin/live-classes/${cls.id}/unpublish`);
      toast.success('Archive retracted');
      onSaved?.(data.class);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to unpublish'));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="publish-archive-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <DialogTitle>Class archive</DialogTitle>
            {isPublished ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1">
                <Eye className="w-3 h-3" /> Published
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-gray-600 gap-1">
                <EyeOff className="w-3 h-3" /> Draft
              </Badge>
            )}
          </div>
          <DialogDescription className="text-xs">
            Curate what students see on the archive page for <strong>{cls.title}</strong>. Add a topics summary
            and paste your Dadan/YouTube iframe. Publish when ready.
          </DialogDescription>
        </DialogHeader>

        {/* Topics summary */}
        <div className="space-y-1.5">
          <Label htmlFor="archive-summary" className="flex items-center gap-1">
            Topics discussed
            <span className="text-xs text-gray-400 font-normal">(a few lines students can search)</span>
          </Label>
          <Textarea
            id="archive-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={SUMMARY_PLACEHOLDER}
            className="min-h-[140px] font-sans text-sm"
            maxLength={10000}
            data-testid="archive-summary-textarea"
          />
          <div className="text-right text-[10px] text-gray-400 tabular-nums">{summary.length}/10000</div>
        </div>

        {/* Recording embed */}
        <div className="space-y-1.5">
          <Label htmlFor="archive-embed" className="flex items-center gap-1">
            Recording embed HTML
            <span className="text-xs text-gray-400 font-normal">(iframe from Dadan, YouTube, Vimeo, Loom, Wistia, or Drive)</span>
          </Label>
          <Textarea
            id="archive-embed"
            value={embed}
            onChange={(e) => setEmbed(e.target.value)}
            placeholder={EMBED_PLACEHOLDER}
            className="min-h-[110px] font-mono text-xs"
            maxLength={10000}
            data-testid="archive-embed-textarea"
          />
          <div className="flex items-start gap-1.5 text-[11px] text-gray-500">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              We strip scripts and only accept iframes from an allowlist of trusted hosts. Anything else is dropped when you save.
            </span>
          </div>
        </div>

        {!canPublish && !isPublished && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Add at least a topics summary or a recording embed to enable publishing.</span>
          </div>
        )}

        {/* Q&A preview + curator link */}
        <div className="space-y-2 border-t pt-4" data-testid="archive-qna-preview">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="flex items-center gap-1.5">
              <MessagesSquare className="w-4 h-4 text-[#0A7EA4]" />
              Q&amp;A that will appear on the archive
            </Label>
            <a
              href={`/live-classes/${cls.id}/questions`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-[#0A7EA4] hover:text-[#086684]"
              data-testid="archive-open-qna-editor"
            >
              Open Q&amp;A editor
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <p className="text-[11px] text-gray-500 -mt-1">
            Whatever is on the Q&amp;A page right now — edited, pinned, replied — is what students will see. Use the editor to clean up before you publish.
          </p>
          {qna.loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading Q&amp;A…
            </div>
          ) : qna.error ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{qna.error}</div>
          ) : qna.total === 0 ? (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-3">
              No questions were asked for this class. The Q&amp;A section will be omitted from the archive.
            </div>
          ) : (
            <div className="rounded border border-gray-200 bg-gray-50 divide-y">
              <div className="px-3 py-2 text-xs text-gray-700 flex items-center gap-3">
                <span><span className="font-semibold text-gray-900">{qna.total}</span> question{qna.total === 1 ? '' : 's'}</span>
                <span className="text-gray-400">·</span>
                <span><span className="font-semibold text-emerald-700">{qna.answered}</span> answered</span>
                {qna.total > qna.questions.length && (
                  <span className="ml-auto text-[10px] text-gray-400">Showing top {qna.questions.length}</span>
                )}
              </div>
              {qna.questions.map(q => (
                <div key={q.id} className="px-3 py-2 flex items-start gap-2 text-xs" data-testid={`archive-qna-preview-item-${q.id}`}>
                  <div className="flex items-center gap-1 text-gray-500 min-w-[42px] pt-0.5">
                    <ThumbsUp className="w-3 h-3" />
                    <span className="tabular-nums">{q.upvote_count}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 line-clamp-2">{q.text}</p>
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500">
                      {q.is_pinned && <span className="inline-flex items-center gap-0.5 text-amber-700"><Pin className="w-2.5 h-2.5" /> pinned</span>}
                      {q.is_answered && <span className="inline-flex items-center gap-0.5 text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5" /> answered</span>}
                      <span>· {q.author_name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={() => onClose?.()} data-testid="archive-modal-close">
            Close
          </Button>
          <Button variant="outline" onClick={saveDraft} disabled={saving || publishing} data-testid="archive-save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save draft'}
          </Button>
          {isPublished ? (
            <Button variant="outline" onClick={doUnpublish} disabled={saving || publishing}
              className="border-red-300 text-red-700 hover:bg-red-50" data-testid="archive-unpublish-btn">
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unpublish'}
            </Button>
          ) : null}
          <Button
            onClick={doPublish}
            disabled={!canPublish || saving || publishing}
            className="bg-[#0A7EA4] hover:bg-[#086684]"
            data-testid="archive-publish-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><Send className="w-4 h-4 mr-1.5" /> {isPublished ? 'Republish' : 'Publish archive'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishArchiveModal;
